import { useEffect, useSyncExternalStore } from "react";

/**
 * Whether anything on screen is still waiting: every mounted `Pending` "…" and every long job a
 * page reports (an upload's progress line). The Design v2 app bar's mark reads it and runs
 * "Draw the P" (DESIGN.md · Loading). A fetch alone never counts: background refetches and polls
 * would make the mark twitch on every page.
 */
let waiting = 0;
const listeners = new Set<() => void>();

function change(by: number) {
    waiting += by;
    listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function isBusy() {
    return waiting > 0;
}

/** Counts this component as waiting while `active`; its unmount stops counting it. */
export function useAppBusy(active: boolean): void {
    useEffect(() => {
        if (!active) return undefined;
        change(1);
        return () => change(-1);
    }, [active]);
}

/** True while anything on screen is waiting. */
export function useIsAppBusy(): boolean {
    return useSyncExternalStore(subscribe, isBusy, isBusy);
}
